import React, { useState } from 'react'
import { TacticKSA } from '@models/index'
import './KSABadge.css'

interface KSABadgeProps {
  ksa: TacticKSA
  compact?: boolean
}

/**
 * Strips " KSA Document" suffix from display names.
 */
function cleanName(name: string): string {
  return name.replace(/\s*KSA\s*Document$/i, '').trim()
}

export const KSABadge: React.FC<KSABadgeProps> = ({ ksa, compact = false }) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const displayName = cleanName(ksa.name)
  const hasDetails = (ksa.knowledge && ksa.knowledge.length > 0) ||
    (ksa.skills && ksa.skills.length > 0) ||
    (ksa.abilities && ksa.abilities.length > 0)

  const renderArray = (label: string, items?: string[]) => {
    if (!items || items.length === 0) return null
    const maxShow = 5
    const displayed = items.slice(0, maxShow)
    const overflow = items.length - maxShow

    return (
      <div className="ksa-array-section">
        <span className="ksa-array-label">{label}: </span>
        <span className="ksa-array-items">
          {displayed.join(', ')}
          {overflow > 0 && (
            <span className="ksa-overflow"> +{overflow} more</span>
          )}
        </span>
      </div>
    )
  }

  return (
    <div className={`ksa-badge ${compact ? 'compact' : ''}`}>
      <span className="ksa-badge-icon" title="Competency Requirement">🎓</span>
      <span className="ksa-badge-text">
        <span className="ksa-name">{displayName}</span>
        {ksa.summary && !isExpanded && (
          <span className="ksa-summary-text"> — {ksa.summary.slice(0, 80)}{ksa.summary.length > 80 ? '...' : ''}</span>
        )}
      </span>
      {hasDetails && (
        <button
          className={`ksa-details-toggle ${isExpanded ? 'expanded' : ''}`}
          onClick={(e) => {
            e.stopPropagation()
            setIsExpanded(!isExpanded)
          }}
          title={isExpanded ? 'Hide details' : 'Show K/S/A details'}
        >
          {isExpanded ? '▼' : '▶'}
        </button>
      )}
      {isExpanded && (
        <div className="ksa-details-panel">
          {renderArray('Knowledge', ksa.knowledge)}
          {renderArray('Skills', ksa.skills)}
          {renderArray('Abilities', ksa.abilities)}
        </div>
      )}
    </div>
  )
}

interface KSABadgeListProps {
  ksas: TacticKSA[]
  compact?: boolean
  maxDisplay?: number
  startExpanded?: boolean
}

export const KSABadgeList: React.FC<KSABadgeListProps> = ({
  ksas,
  compact = false,
  maxDisplay = 5,
  startExpanded = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(startExpanded)

  if (!ksas || ksas.length === 0) return null

  // Collapsed: show compact summary badge
  if (!isExpanded) {
    const names = ksas.slice(0, 3).map(k => cleanName(k.name))
    const preview = names.join(', ') + (ksas.length > 3 ? '...' : '')
    return (
      <div className="ksa-badge-list">
        <button
          className="ksa-summary-toggle"
          onClick={() => setIsExpanded(true)}
          title={`${ksas.length} Competencies: ${ksas.map(k => cleanName(k.name)).join(', ')}`}
        >
          <span className="ksa-badge-icon">🎓</span>
          <span className="ksa-summary-count">{ksas.length} Competenc{ksas.length !== 1 ? 'ies' : 'y'}</span>
          <span className="ksa-summary-preview">{preview}</span>
          <span className="ksa-summary-expand">▶</span>
        </button>
      </div>
    )
  }

  // Expanded: show full KSA badges
  const displayed = ksas.slice(0, maxDisplay)
  const hasMore = ksas.length > maxDisplay

  return (
    <div className="ksa-badge-list expanded">
      <button
        className="ksa-summary-toggle active"
        onClick={() => setIsExpanded(false)}
      >
        <span className="ksa-badge-icon">🎓</span>
        <span className="ksa-summary-count">{ksas.length} Competenc{ksas.length !== 1 ? 'ies' : 'y'}</span>
        <span className="ksa-summary-expand">▼</span>
      </button>
      {displayed.map((k) => (
        <KSABadge key={k.id} ksa={k} compact={compact} />
      ))}
      {hasMore && (
        <span className="ksa-badge-overflow">
          +{ksas.length - maxDisplay} more
        </span>
      )}
    </div>
  )
}
